import React, { useEffect, useCallback } from 'react'; // Removed unused useRef, added useCallback
import { useHistory, COMMAND_TYPES } from '../../context/HistoryContext';
import { useLayers } from '../../context/LayerContext';
import { useContainers } from '../../context/ContainerContext';
import './UndoRedoButtons.css';

const UndoRedoButtons = ({ 
  // Token-related props
  tokens = [],
  onUpdateToken,
  onDeleteToken,
  onRestoreToken,
  onTokenSelect,
  tokenGroups = [],
  onCreateTokenGroup,
  onRemoveTokenGroup,
  onCreateTokenGroupDirect,
  onRemoveTokenGroupDirect,
  onUpdateTokenGroupDirect
}) => {
  const { undo, redo, canUndo, canRedo, applyDelta, flushPending } = useHistory();
  const { layers, updateLayer, createLayer, deleteLayer, deleteLayers, reorderLayers, setActiveLayerById, restoreLayer, restoreLayerGroups } = useLayers();
  const { updateContainerDirect, deleteContainerDirect, addContainerDirect, setSelectedContainerId } = useContainers(); // Removed unused: updateContainer, deleteContainer, addContainer
  
  // Handle undo/redo command execution for tokens
  const handleTokenCommand = useCallback((command, operation) => {
    
    switch (command.type) {
      case COMMAND_TYPES.ADD_TOKEN:
        if (operation === 'undo') {
          // Remove the added token
          if (onDeleteToken) {
            onDeleteToken(command.targetId);
          }
          // Restore previous selection
          if (onTokenSelect && command.selectedTokenIds) {
            onTokenSelect(command.selectedTokenIds);
          } else if (onTokenSelect) {
            onTokenSelect([]);
          }
        } else {
          // Re-add the token
          if (onRestoreToken) {
            onRestoreToken(command.newState);
            // Restore selection to the restored token
            if (onTokenSelect) {
              onTokenSelect(command.targetId);
            }
          }
        }
        break;
        
      case COMMAND_TYPES.UPDATE_TOKEN:
        if (operation === 'undo') {
          // Restore previous state
          if (onUpdateToken) {
            onUpdateToken(command.targetId, command.previousState);
          }
          // Restore selection
          if (onTokenSelect && command.selectedTokenIds) {
            onTokenSelect(command.selectedTokenIds);
          }
        } else {
          // Apply new state
          if (onUpdateToken) {
            onUpdateToken(command.targetId, command.newState);
          }
          // Restore selection
          if (onTokenSelect && command.selectedTokenIds) {
            onTokenSelect(command.selectedTokenIds);
          }
        }
        break;
        
      case COMMAND_TYPES.DELETE_TOKEN:
        if (operation === 'undo') {
          // Re-add the deleted token
          if (onRestoreToken) {
            onRestoreToken(command.previousState);
            // Restore selection to the restored token
            if (onTokenSelect) {
              onTokenSelect(command.targetId);
            }
          }
        } else {
          // Re-delete the token
          if (onDeleteToken) {
            onDeleteToken(command.targetId);
          }
          // Apply next selection logic
          if (onTokenSelect && command.nextSelectedTokenIds) {
            onTokenSelect(command.nextSelectedTokenIds);
          } else if (onTokenSelect) {
            onTokenSelect([]);
          }
        }
        break;
        
      case COMMAND_TYPES.MOVE_TOKEN:
        if (operation === 'undo') {
          // Restore previous states
          if (onUpdateToken && command.previousStates) {
            command.previousStates.forEach(state => {
              onUpdateToken(state.id, state);
            });
          }
        } else {
          // Apply new states
          if (onUpdateToken && command.newStates) {
            command.newStates.forEach(state => {
              onUpdateToken(state.id, state);
            });
          }
        }
        // Restore selection
        if (onTokenSelect && command.selectedTokenIds) {
          onTokenSelect(command.selectedTokenIds);
        }
        break;

      case 'BATCH_UPDATE_TOKENS':
        if (operation === 'undo') {
          // Restore previous states for all tokens
          if (onUpdateToken && command.previousStates) {
            command.previousStates.forEach(state => {
              const { id, ...updateData } = state;
              onUpdateToken(id, updateData);
            });
          }
        } else {
          // Apply new states for all tokens
          if (onUpdateToken && command.newStates) {
            command.newStates.forEach(state => {
              const { id, ...updateData } = state;
              onUpdateToken(id, updateData);
            });
          }
        }
        // Restore selection
        if (onTokenSelect && command.selectedTokenIds) {
          onTokenSelect(command.selectedTokenIds);
        }
        break;
        
      case COMMAND_TYPES.CREATE_TOKEN_GROUP:
        if (operation === 'undo') {
          // Remove the created group directly (no new command)
          if (onRemoveTokenGroupDirect && command.groupData) {
            onRemoveTokenGroupDirect(command.groupData.id);
          }
          // Restore previous selection
          if (onTokenSelect && command.selectedTokenIds) {
            onTokenSelect(command.selectedTokenIds);
          }
        } else {
          // Re-create the group directly (no new command)
          if (onCreateTokenGroupDirect && command.groupData) {
            onCreateTokenGroupDirect(command.groupData);
          }
          // Select the group tokens
          if (onTokenSelect && command.groupData.tokenIds) {
            onTokenSelect(command.groupData.tokenIds);
          }
        }
        break;
        
      case COMMAND_TYPES.REMOVE_TOKEN_GROUP:
        if (operation === 'undo') {
          // Re-create the removed group directly (no new command)
          if (onCreateTokenGroupDirect && command.groupData) {
            onCreateTokenGroupDirect(command.groupData);
          }
          // Select the group tokens
          if (onTokenSelect && command.groupData.tokenIds) {
            onTokenSelect(command.groupData.tokenIds);
          }
        } else {
          // Re-remove the group directly (no new command)
          if (onRemoveTokenGroupDirect && command.groupData) {
            onRemoveTokenGroupDirect(command.groupData.id);
          }
          // Restore previous selection
          if (onTokenSelect && command.selectedTokenIds) {
            onTokenSelect(command.selectedTokenIds);
          }
        }
        break;

      case 'UPDATE_TOKEN_GROUP_COLOR':
        if (operation === 'undo') {
          // Restore previous color
          if (onUpdateTokenGroupDirect && command.groupId && command.previousColor !== undefined) {
            onUpdateTokenGroupDirect(command.groupId, { color: command.previousColor });
          }
        } else {
          // Apply new color
          if (onUpdateTokenGroupDirect && command.groupId && command.newColor !== undefined) {
            onUpdateTokenGroupDirect(command.groupId, { color: command.newColor });
          }
        }
        // Restore selection
        if (onTokenSelect && command.selectedTokenIds) {
          onTokenSelect(command.selectedTokenIds);
        }
        break;
        
      default:
        console.warn('[UndoRedoButtons] Unknown token command type:', command.type);
    }
  }, [onDeleteToken, onTokenSelect, onRestoreToken, onUpdateToken, onCreateTokenGroupDirect, onRemoveTokenGroupDirect, onUpdateTokenGroupDirect]);
  
  // Handle undo/redo command execution for containers
  const handleContainerCommand = useCallback((command, operation) => {
    switch (command.type) {
      case COMMAND_TYPES.UPDATE_CONTAINER:
        if (operation === 'undo') {
          // Restore previous state
          if (command.previousState) {
            // Extract only the changed properties to avoid overwriting everything
            const updates = {};
            if (command.updateType) {
              const updateTypes = command.updateType.split(',');
              updateTypes.forEach(type => {
                if (command.previousState[type] !== undefined) {
                  updates[type] = command.previousState[type];
                }
              });
            } else {
              // Fallback: restore all properties from previousState
              Object.keys(command.newState || {}).forEach(key => {
                if (command.previousState[key] !== undefined) {
                  updates[key] = command.previousState[key];
                }
              });
            }
            updateContainerDirect(command.targetId, updates);
          }
        } else {
          // Apply new state
          if (command.newState) {
            const updates = {};
            if (command.updateType) {
              const updateTypes = command.updateType.split(',');
              updateTypes.forEach(type => {
                if (command.newState[type] !== undefined) {
                  updates[type] = command.newState[type];
                }
              });
            } else {
              // Fallback: apply all properties from newState
              Object.keys(command.newState).forEach(key => {
                if (command.newState[key] !== undefined) {
                  updates[key] = command.newState[key];
                }
              });
            }
            updateContainerDirect(command.targetId, updates);
          }
        }
        break;
        
      case COMMAND_TYPES.TOGGLE_LOCK_CONTAINER:
        if (operation === 'undo') {
          // Restore previous lock state
          updateContainerDirect(command.targetId, { locked: command.previousLocked });
        } else {
          // Apply new lock state
          updateContainerDirect(command.targetId, { locked: command.newLocked });
        }
        break;
        
      case COMMAND_TYPES.CHANGE_CONTAINER_Z_INDEX:
        if (operation === 'undo') {
          // Restore previous z-indices
          updateContainerDirect(command.targetId, { zIndex: command.previousZIndex });
          if (command.affectedContainerId) {
            updateContainerDirect(command.affectedContainerId, { zIndex: command.affectedPreviousZIndex });
          }
        } else {
          // Apply new z-indices
          updateContainerDirect(command.targetId, { zIndex: command.newZIndex });
          if (command.affectedContainerId) {
            updateContainerDirect(command.affectedContainerId, { zIndex: command.affectedNewZIndex });
          }
        }
        break;
        
      case COMMAND_TYPES.ADD_CONTAINER:
        if (operation === 'undo') {
          // Remove the added container
          deleteContainerDirect(command.targetId);
        } else {
          // Re-add the container with the same data
          if (command.newState) {
            // Restore the image data if it was compressed
            const containerData = { ...command.newState };
            if (containerData.image && typeof containerData.image.src === 'string' && containerData.image.src.startsWith('[IMAGE_')) {
              console.warn('[UndoRedoButtons] Cannot restore container with compressed image data');
              return;
            }
            addContainerDirect(containerData);
            setSelectedContainerId(command.targetId);
          }
        }
        break;
        
      case COMMAND_TYPES.DELETE_CONTAINER:
        if (operation === 'undo') {
          // Restore the deleted container
          if (command.previousState) {
            // Restore the image data if it was compressed
            const containerData = { ...command.previousState };
            if (containerData.image && typeof containerData.image.src === 'string' && containerData.image.src.startsWith('[IMAGE_')) {
              console.warn('[UndoRedoButtons] Cannot restore container with compressed image data');
              return;
            }
            addContainerDirect(containerData);
            setSelectedContainerId(command.targetId);
          }
        } else {
          // Re-delete the container
          deleteContainerDirect(command.targetId);
        }
        break;
        
      case COMMAND_TYPES.DUPLICATE_CONTAINER:
        if (operation === 'undo') {
          // Remove the duplicated container
          deleteContainerDirect(command.targetId);
        } else {
          // Re-add the duplicated container
          if (command.newState) {
            // Restore the image data if it was compressed
            const containerData = { ...command.newState };
            if (containerData.image && typeof containerData.image.src === 'string' && containerData.image.src.startsWith('[IMAGE_')) {
              console.warn('[UndoRedoButtons] Cannot restore duplicated container with compressed image data');
              return;
            }
            addContainerDirect(containerData);
            setSelectedContainerId(command.targetId);
          }
        }
        break;
        
      default:
        console.warn('[UndoRedoButtons] Unknown container command type:', command.type);
    }
  }, [updateContainerDirect, deleteContainerDirect, addContainerDirect, setSelectedContainerId]);
  
  // Handle undo/redo command execution for layers
  const handleLayerCommand = useCallback((command, operation) => {
    
    switch (command.type) {
      case COMMAND_TYPES.ADD_LAYER:
        if (operation === 'undo') {
          // Remove the added layer
          if (deleteLayer) {
            deleteLayer(command.targetId);
            // Restore previous active layer
            if (setActiveLayerById && command.previousActiveLayerId) {
              setActiveLayerById(command.previousActiveLayerId);
            }
          }
        } else {
          // Re-add the layer (redo)
          if (command.newState) {
            // Check if layer already exists to prevent duplication
            const existingLayer = layers.find(l => l.layer_id === command.targetId);
            if (!existingLayer) {
              // Use restoreLayer instead of createLayer to maintain the same ID
              if (restoreLayer) {
                restoreLayer(command.newState);
              } else if (createLayer) {
                // Fallback to createLayer if restoreLayer is not available
                const layerData = {
                  layer_name: command.newState.layer_name,
                  layer_data: command.newState.layer_data,
                  is_wall_layer: command.newState.is_wall_layer,
                  is_visible: command.newState.is_visible
                };
                createLayer(layerData);
              }
            }
            // Restore active layer
            if (setActiveLayerById && command.newActiveLayerId) {
              setActiveLayerById(command.newActiveLayerId);
            }
          }
        }
        break;
        
      case COMMAND_TYPES.DELETE_LAYER:
        if (operation === 'undo') {
          // Re-add the deleted layer
          if (restoreLayer && command.previousState) {
            restoreLayer(command.previousState);
            // Restore previous active layer
            if (setActiveLayerById && command.previousActiveLayerId) {
              setActiveLayerById(command.previousActiveLayerId);
            }
            // Restore previous layer groups if provided
            if (command.previousLayerGroups && Array.isArray(command.previousLayerGroups) && restoreLayerGroups) {
              restoreLayerGroups(command.previousLayerGroups);
            }
          }
        } else {
          // Re-delete the layer
          if (deleteLayer) {
            deleteLayer(command.targetId);
          }
          // Apply new groups state that resulted from the original deletion (disbanded groups)
          if (command.newLayerGroups && Array.isArray(command.newLayerGroups) && restoreLayerGroups) {
            restoreLayerGroups(command.newLayerGroups);
          }
        }
        break;
        
      case COMMAND_TYPES.UPDATE_LAYER:
        if (operation === 'undo') {
          // Restore previous state
          if (updateLayer && command.previousState) {
            const updateData = {};
            if (command.updateType === 'visibility') {
              updateData.is_visible = command.previousState.is_visible;
            } else if (command.updateType === 'wall_layer') {
              updateData.is_wall_layer = command.previousState.is_wall_layer;
            } else if (command.updateType === 'name') {
              updateData.layer_name = command.previousState.layer_name;
            } else {
              // General update - restore all changed properties
              Object.keys(command.newState).forEach(key => {
                if (command.previousState[key] !== command.newState[key]) {
                  updateData[key] = command.previousState[key];
                }
              });
            }
            updateLayer(command.targetId, updateData);
          }
        } else {
          // Apply new state
          if (updateLayer && command.newState) {
            const updateData = {};
            if (command.updateType === 'visibility') {
              updateData.is_visible = command.newState.is_visible;
            } else if (command.updateType === 'wall_layer') {
              updateData.is_wall_layer = command.newState.is_wall_layer;
            } else if (command.updateType === 'name') {
              updateData.layer_name = command.newState.layer_name;
            } else {
              // General update - apply all changed properties
              Object.keys(command.newState).forEach(key => {
                if (command.previousState[key] !== command.newState[key]) {
                  updateData[key] = command.newState[key];
                }
              });
            }
            updateLayer(command.targetId, updateData);
          }
        }
        break;

      // Layer group operations using full groups snapshots
      case COMMAND_TYPES.CREATE_LAYER_GROUP:
      case COMMAND_TYPES.DELETE_LAYER_GROUP:
      case COMMAND_TYPES.UPDATE_LAYER_GROUP_COLOR: {
        if (operation === 'undo') {
          if (restoreLayerGroups && command.previousGroups) {
            restoreLayerGroups(command.previousGroups);
          }
        } else {
          if (restoreLayerGroups && command.newGroups) {
            restoreLayerGroups(command.newGroups);
          }
        }
        break;
      }
        
      case COMMAND_TYPES.REORDER_LAYERS:
        if (operation === 'undo') {
          // Restore previous order
          if (reorderLayers && command.previousOrder) {
            reorderLayers(command.previousOrder);
          }
        } else {
          // Apply new order
          if (reorderLayers && command.newOrder) {
            reorderLayers(command.newOrder);
          }
        }
        break;
        
      case COMMAND_TYPES.ADD_IMAGE_TO_EXISTING_LAYER:
        if (operation === 'undo') {
          // Remove the container that was added to the existing layer
          deleteContainerDirect(command.containerId);
          // Restore previous active layer
          if (setActiveLayerById && command.previousActiveLayerId) {
            setActiveLayerById(command.previousActiveLayerId);
          }
        } else {
          // For redo, restore the container that was removed
          if (command.containerData) {
            // Re-add the container with the stored data
            addContainerDirect(command.containerData);
            setSelectedContainerId(command.containerId);
          }
          // Set the active layer back to the target layer
          if (setActiveLayerById && command.newActiveLayerId) {
            setActiveLayerById(command.newActiveLayerId);
          }
        }
        break;
        
      case COMMAND_TYPES.ADD_IMAGE_WITH_NEW_LAYERS:
        if (operation === 'undo') {
          // Remove the container that was added
          deleteContainerDirect(command.containerId);
          
          // Remove both layers that were created using the new batch delete function
          if (deleteLayers && command.targetLayerId && command.fowLayerId) {
            const layersToDelete = [command.targetLayerId, command.fowLayerId];
            deleteLayers(layersToDelete);
          } else {
            console.warn('Missing deleteLayers function or layer IDs');
          }
          
          // Restore previous active layer
          if (setActiveLayerById && command.previousActiveLayerId) {
            setActiveLayerById(command.previousActiveLayerId);
          }
        } else {
          // For redo, restore both layers and the container
          // Restore both layers using the stored layer states
          if (restoreLayer && command.newLayerState && command.newFowLayerState) {
            restoreLayer(command.newLayerState);
            restoreLayer(command.newFowLayerState);
          }
          
          // Restore the container
          if (command.containerData) {
            addContainerDirect(command.containerData);
            setSelectedContainerId(command.containerData.id);
          }
          
          // Restore active layer
          if (setActiveLayerById && command.newActiveLayerId) {
            setActiveLayerById(command.newActiveLayerId);
          }
        }
        break;
        
      default:
        console.warn('[UndoRedoButtons] Unknown layer command type:', command.type);
    }
    

  }, [layers, deleteLayer, setActiveLayerById, restoreLayer, createLayer, updateLayer, reorderLayers, deleteContainerDirect, addContainerDirect, setSelectedContainerId, deleteLayers, restoreLayerGroups]);
  
  // Handle drawing commands with differential storage
  const handleDrawingCommand = useCallback((command, operation) => {
    
    const layer = layers.find(l => l.layer_id === command.targetId);
    if (!layer) {
      console.warn('[UndoRedoButtons] Layer not found for drawing command:', command.targetId);
      return;
    }
    
    let newState;
    if (command.delta) {
      // Use differential storage
      let currentState;
      try {
        // Handle cases where layer_data might be undefined, null, or invalid JSON
        currentState = layer.layer_data ? JSON.parse(layer.layer_data) : {};
      } catch (error) {
        console.warn('[UndoRedoButtons] Failed to parse layer data, using empty state:', error);
        currentState = {};
      }
      newState = applyDelta(currentState, command.delta, operation === 'undo');
    } else {
      // Fallback to full state
      newState = operation === 'undo' ? command.previousState : command.newState;
    }
    
    // Ensure newState is valid before stringifying
    if (!newState) {
      console.warn('[UndoRedoButtons] newState is null/undefined, using empty object');
      newState = {};
    }
    

    
    updateLayer(command.targetId, {
      ...layer,
      layer_data: JSON.stringify(newState)
    });
  }, [layers, applyDelta, updateLayer]);
  
  // Handle batch commands
  const handleBatchCommand = useCallback((batchCommand, operation) => {
    
    const commands = operation === 'undo' 
      ? [...batchCommand.commands].reverse() // Reverse order for undo
      : batchCommand.commands;
    
    commands.forEach(command => {
      if (command.type.includes('TOKEN')) {
        handleTokenCommand(command, operation);
      } else if (command.type.includes('CONTAINER')) {
        handleContainerCommand(command, operation);
      } else if (command.type.includes('LAYER')) {
        handleLayerCommand(command, operation);
      } else {
        handleDrawingCommand(command, operation);
      }
    });
  }, [handleTokenCommand, handleContainerCommand, handleLayerCommand, handleDrawingCommand]);
  
  const handleUndo = useCallback(() => {
    const command = undo();
    if (command) {
      
      // Handle batch commands
      if (command.type === COMMAND_TYPES.BATCH) {
        handleBatchCommand(command, 'undo');
      } else if (command.type.includes('TOKEN')) {
        // Handle token commands
        handleTokenCommand(command, 'undo');
      } else if (command.type.includes('CONTAINER')) {
        // Handle container commands
        handleContainerCommand(command, 'undo');
      } else if (command.type.includes('LAYER')) {
        // Handle layer commands
        handleLayerCommand(command, 'undo');
      } else {
        // Handle drawing commands
        handleDrawingCommand(command, 'undo');
      }
    }
  }, [undo, handleTokenCommand, handleContainerCommand, handleLayerCommand, handleDrawingCommand, handleBatchCommand]);
  
  const handleRedo = useCallback(() => {
    const command = redo();
    if (command) {
      
      // Handle batch commands
      if (command.type === COMMAND_TYPES.BATCH) {
        handleBatchCommand(command, 'redo');
      } else if (command.type.includes('TOKEN')) {
        // Handle token commands
        handleTokenCommand(command, 'redo');
      } else if (command.type.includes('CONTAINER')) {
        // Handle container commands
        handleContainerCommand(command, 'redo');
      } else if (command.type.includes('LAYER')) {
        // Handle layer commands
        handleLayerCommand(command, 'redo');
      } else {
        // Handle drawing commands
        handleDrawingCommand(command, 'redo');
      }
    }
  }, [redo, handleTokenCommand, handleContainerCommand, handleLayerCommand, handleDrawingCommand, handleBatchCommand]);
  
  // Add keyboard event handling
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle Undo/Redo when no specific input is focused
      const activeElement = document.activeElement;
      const isInputFocused = activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'TEXTAREA' || 
        activeElement.contentEditable === 'true'
      );
      
      if (isInputFocused) return; // Don't interfere with input editing
      
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndo()) {
          handleUndo();
        }
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        if (canRedo()) {
          handleRedo();
        }
      }
    };
    
    // Handle page unload to save pending commands
    const handleBeforeUnload = () => {
      if (flushPending) {
        flushPending();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [canUndo, canRedo, handleUndo, handleRedo, flushPending]);
  
  return (
    <div className="undo-redo-buttons">
      <button 
        className="undo-btn"
        onClick={handleUndo} 
        disabled={!canUndo()}
        title="Undo (Ctrl+Z)"
        aria-label="Undo"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 7v6h6" />
          <path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13" />
        </svg>
      </button>
      <button 
        className="redo-btn"
        onClick={handleRedo} 
        disabled={!canRedo()}
        title="Redo (Ctrl+Y)"
        aria-label="Redo"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 7v6h-6" />
          <path d="M3 17a9 9 0 019-9 9 9 0 016 2.3L21 13" />
        </svg>
      </button>
    </div>
  );
};

export default UndoRedoButtons; 